import { useSnackbar } from 'notistack';
import React, { createContext, useContext } from 'react';
import { MediaItemDetailed, extractResultsFromResponse } from '../types';
import { apiFetch, REACT_APP_API_ROOT } from '../utilities/api';
import { useConfig } from './ConfigContext';
import { useSpinner } from './SpinnerContext';

interface MediaItemsContextProps {
  fetchMediaItemsForGeom: (
    feature: any,
    layerConfig: any
  ) => Promise<MediaItemDetailed[]>;
  fetchMediaItemsForElement: (
    tableName: string,
    id: string
  ) => Promise<MediaItemDetailed[]>;
}

const MediaItemsContext = createContext<MediaItemsContextProps>({
  fetchMediaItemsForGeom: async () => [],
  fetchMediaItemsForElement: async () => [],
});

export const useMediaItems = () => useContext(MediaItemsContext);

export const MediaItemsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const config = useConfig();
  const { enqueueSnackbar } = useSnackbar();
  const { startLoading, stopLoading } = useSpinner();

  const fetchMediaItemsForGeom = async (feature: any, layerConfig: any) => {
    startLoading();

    try {
      const allMediaItems = [];

      if (!layerConfig.joinedData) {
        console.warn("No joinedData configuration found in layer config");
        return [];
      }

      for (const joinedData of layerConfig.joinedData) {
        const mediaEndpoint = config.apiEndpoints.find(
          (e) => e.tableName.toLowerCase() === "media"
        )?.api_endpoint;

        if (!mediaEndpoint) {
          throw new Error("Media endpoint not found in config");
        }

        const baseUrl = `${REACT_APP_API_ROOT}${mediaEndpoint}`;
        const params = new URLSearchParams();

        // If this layer config has parentField defined, use it
        if (joinedData.templateFields?.parentField) {
          const parentFieldValue = feature.properties[joinedData.templateFields.parentField];
          if (parentFieldValue) {
            
            // If parentTargetTable is specified, use it to find the endpoint
            const targetTable = joinedData.templateFields.parentTargetTable || 
                               layerConfig.entityMapping?.table || 
                               layerConfig.id;
            
            // Find matching endpoint for the parent table name
            const parentEndpoint = config.apiEndpoints.find(
              (e) => e.name.toLowerCase() === targetTable.toLowerCase() || 
                     e.tableName.toLowerCase() === targetTable.toLowerCase()
            );
            
            if (parentEndpoint) {
              params.append(parentEndpoint.queryParam, parentFieldValue.toString());
            } else {
              console.warn(`Could not find endpoint for parent table: ${targetTable}`);
            }
          }
        }

        // Add any configured filters only if enableFilters is true
        if (joinedData.enableFilters && joinedData.filters) {
          Object.entries(joinedData.filters).forEach(([key, value]) => {
            // Check if the value is a template string like "{id}" and replace with feature property
            let actualValue = value;
            if (typeof value === 'string' && value.match(/^\{.*\}$/)) {
              const propertyName = value.substring(1, value.length - 1);
              if (feature.properties && propertyName in feature.properties) {
                actualValue = feature.properties[propertyName];
              } else {
                console.warn(`Property ${propertyName} not found in feature properties`, feature.properties);
              }
            }
            
            // Get the correct query parameter name from apiEndpoints
            const endpoint = config.apiEndpoints.find(
              (e) => e.relationshipKey === key
            );
            if (endpoint) {
              params.append(endpoint.queryParam, actualValue as string);
            } else {
              params.append(key, actualValue as string);
            }
          });
        }

        const finalUrl = `${baseUrl}?${params.toString()}`;

        try {
          const response = await apiFetch(finalUrl);
          const data = extractResultsFromResponse<MediaItemDetailed>(response);
          allMediaItems.push(...data);
        } catch (error) {
          console.error("Failed to fetch with filtered URL:", error);
          enqueueSnackbar(
            "Unable to filter media items. Showing all items instead.",
            { variant: "warning" }
          );
          // Fetch all items instead
          const fallbackResponse = await apiFetch(baseUrl);
          const fallbackData = extractResultsFromResponse<MediaItemDetailed>(fallbackResponse);
          allMediaItems.push(...fallbackData);
        }
      }

      return allMediaItems;
    } catch (error) {
      console.error("Failed to fetch media items:", error);
      enqueueSnackbar("Error loading media items", { variant: "error" });
      return [];
    } finally {
      stopLoading();
    }
  };

  const fetchMediaItemsForElement = async (tableName: string, id: string) => {
    try {
      // Updated to be case-insensitive
      const mediaEndpoint = config.apiEndpoints.find(
        (e) => e.tableName.toLowerCase() === "media"
      )?.api_endpoint;

      if (!mediaEndpoint) {
        throw new Error("Media endpoint not found in config");
      }

      const url = `${REACT_APP_API_ROOT}${mediaEndpoint}${tableName}/${id}`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Failed to fetch media items:", error);
      return [];
    }
  };

  return (
    <MediaItemsContext.Provider
      value={{
        fetchMediaItemsForGeom,
        fetchMediaItemsForElement,
      }}
    >
      {children}
    </MediaItemsContext.Provider>
  );
};
